import { Show, createSignal, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Heading, Text } from '~ui/text';
import { Button } from '~ui/button';
import { Input } from '~ui/input';
import { LOGIN, db } from '~/lib/db';

// What a signed-out visitor gets instead of the app.
//
// The app authenticates itself rather than handing off to the marketing site.
// Sending people there cannot work outside production: supabase-js keeps the
// session in localStorage, which is origin-scoped, so a session created on the
// site is invisible to the app on any other port — and with VITE_SITE_URL unset
// the link resolved to this app's own /login, which the dev server answers with
// the app shell. Pressing sign in reloaded the sign-in screen, forever.
//
// Signing in here writes the session to whatever origin the app is on, so it
// works in development and in production without either knowing about the other.
// Account creation stays on the site; this only takes an existing one.
// Google's own mark, in its own colours. It is a brand asset rather than one of
// the app's line icons, which is why it does not live in the shared icon set and
// does not take `currentColor` — Google's terms are specific about it being
// reproduced as published, and it reads correctly on both themes as-is.
const GoogleIcon = () => (
    <svg class='h-4 w-4' viewBox='0 0 48 48' aria-hidden='true'>
        <path
            fill='#EA4335'
            d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'
        />
        <path fill='#4285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z' />
        <path fill='#FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z' />
        <path fill='#34A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z' />
    </svg>
);

const SignIn: Component = () => {
    const [email, setEmail] = createSignal('');
    const [password, setPassword] = createSignal('');
    const [busy, setBusy] = createSignal(false);
    const [error, setError] = createSignal<string | null>(null);

    const valid = () => email().trim() !== '' && password() !== '';

    // No success branch: onAuthStateChange fires and the gate swaps this whole
    // screen for the app, so there is nothing here to do about it.
    const submit = async () => {
        if (busy() || !valid()) return;
        setBusy(true);
        setError(null);
        const { error: failure } = await db.auth.signInWithPassword({ email: email().trim(), password: password() });
        setBusy(false);
        if (failure) setError(failure.message);
    };

    /**
     * Google comes back to the app, not to the site's /auth/callback.
     *
     * The session has to be written on the origin that will read it, and that is
     * this one — sending the round trip to the site would leave the token in the
     * site's localStorage and the app exactly as signed out as it started.
     * BASE_URL keeps it right in both places: `/` on the dev server, `/app/` in
     * the build. Supabase exchanges the code on return by itself, and
     * onAuthStateChange does the rest.
     *
     * This URL must be on the project's allowed redirect list, same as the
     * site's callback is.
     */
    const google = async () => {
        if (busy()) return;
        setBusy(true);
        setError(null);
        const { error: failure } = await db.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: `${window.location.origin}${import.meta.env.BASE_URL}` },
        });
        // On success the browser has already navigated away, so this only runs
        // when the redirect could not be started at all.
        if (failure) {
            setError(failure.message);
            setBusy(false);
        }
    };

    const Field: Component<{ label: string; children: any; last?: boolean }> = (props) => (
        <div class={`flex h-row items-center px-2 ${props.last ? '' : 'border-b'}`}>
            <Text tone='muted' class='w-20 shrink-0'>
                {props.label}
            </Text>
            {props.children}
        </div>
    );

    return (
        <Panel class='items-center justify-center'>
            <div class='w-72' onKeyDown={(e) => e.key === 'Enter' && void submit()}>
                <Heading level={1} class='block pb-4 text-center'>
                    <Text tone='muted'>Clean</Text>Dividends
                </Heading>

                <div class='border'>
                    <Field label='Email'>
                        <Input bare type='email' autocomplete='username' value={email()} onInput={(e) => setEmail(e.currentTarget.value)} />
                    </Field>
                    <Field label='Password' last>
                        <Input bare type='password' autocomplete='current-password' value={password()} onInput={(e) => setPassword(e.currentTarget.value)} />
                    </Field>
                </div>

                <Button class='mt-2 w-full' label={busy() ? 'Signing in…' : 'Sign in'} disabled={busy() || !valid()} onClick={() => void submit()} />

                {/* A rule with the word in it, rather than a second heading:
                    the two are alternatives, not a sequence. */}
                <div class='flex items-center gap-2 py-3'>
                    <div class='h-px flex-1 bg-border' />
                    <Text tone='muted'>or</Text>
                    <div class='h-px flex-1 bg-border' />
                </div>

                <Button class='w-full' icon={<GoogleIcon />} label='Continue with Google' disabled={busy()} onClick={() => void google()} />

                <Show when={error()}>
                    <Text tone='red' class='block pt-2 text-center'>
                        {error()}
                    </Text>
                </Show>

                <Text tone='muted' class='block pt-4 text-center'>
                    No account?{' '}
                    <a href={LOGIN} class='underline'>
                        Sign up on the site
                    </a>
                </Text>
            </div>
        </Panel>
    );
};

export default SignIn;
